/**
 * How a time of day reads across the agent app: the 12 hour clock ("2:30 PM")
 * or the 24 hour clock ("14:30"). Settings, Appearance, Time (Shardul,
 * 21 Oct 26: "set 12 hour to 24 hour format for the time everywhere on the
 * app that displays").
 *
 * The choice lives in this browser, like the theme (localStorage
 * fly-agent-time-format); nothing is sent anywhere. Every place that writes
 * a clock reads it through clockFrom / clockOf / clockPattern here, so one
 * switch changes them all. Pages that are open re-draw through
 * useTimeFormat (AgentPageLayout keys its content on it).
 *
 * Dates keep the house format (dd Mon yy); only the clock part changes.
 */
import { useSyncExternalStore } from 'react';

export type TimeFormat = '12h' | '24h';

export const TIME_FORMAT_KEY = 'fly-agent-time-format';
export const TIME_FORMAT_EVENT = 'fly-agent-time-format';
/** 12 hour by default: the clock the owners asked for (Coming up, 08 Oct 26; post times, 07 Oct 26). */
export const DEFAULT_TIME_FORMAT: TimeFormat = '12h';

export const TIME_FORMATS: ReadonlyArray<{ id: TimeFormat; title: string; sample: string }> = [
  { id: '12h', title: '12 hour', sample: '2:30 PM' },
  { id: '24h', title: '24 hour', sample: '14:30' },
];

export function isTimeFormat(value: unknown): value is TimeFormat {
  return value === '12h' || value === '24h';
}

/**
 * The last pick, for where storage cannot hold it (private mode, or no
 * window at all). Only setTimeFormat writes it, and only the Appearance tab
 * calls that, so a server never moves off the default.
 */
let remembered: TimeFormat = DEFAULT_TIME_FORMAT;

/** The format chosen in this browser, else the default. Safe on the server and in private mode. */
export function timeFormat(): TimeFormat {
  if (typeof window === 'undefined') return remembered;
  try {
    const saved = window.localStorage.getItem(TIME_FORMAT_KEY);
    return isTimeFormat(saved) ? saved : remembered;
  } catch {
    return remembered;
  }
}

/** Remember a format in this browser and tell every open page. */
export function setTimeFormat(next: TimeFormat): void {
  remembered = next;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TIME_FORMAT_KEY, next);
  } catch {
    /* private mode: the choice holds until the page is left */
  }
  window.dispatchEvent(new Event(TIME_FORMAT_EVENT));
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(TIME_FORMAT_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(TIME_FORMAT_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

const serverFormat = () => DEFAULT_TIME_FORMAT;

/** The current format, re-rendering when it changes (here or in another tab). */
export function useTimeFormat(): TimeFormat {
  return useSyncExternalStore(subscribe, timeFormat, serverFormat);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** An hour (0 to 23) and minute as the chosen clock reads them: "14:05" or "2:05 PM". */
export function clockFrom(hour: number, minute: number, format: TimeFormat = timeFormat()): string {
  const h = ((Math.trunc(hour) % 24) + 24) % 24;
  const m = pad(((Math.trunc(minute) % 60) + 60) % 60);
  if (format === '24h') return `${pad(h)}:${m}`;
  return `${h % 12 || 12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

const ZONE_CLOCKS = new Map<string, Intl.DateTimeFormat | null>();

/** One hour-and-minute reader per zone; null for a name that is not a real zone. */
function zoneClock(zone: string): Intl.DateTimeFormat | null {
  if (ZONE_CLOCKS.has(zone)) return ZONE_CLOCKS.get(zone) ?? null;
  let made: Intl.DateTimeFormat | null = null;
  try {
    made = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  } catch {
    made = null;
  }
  ZONE_CLOCKS.set(zone, made);
  return made;
}

/**
 * The clock of an instant, in `zone` when one is named and real, else the
 * browser's; '' for a value that is not a time.
 */
export function clockOf(value: Date | string | number, zone?: string | null, format: TimeFormat = timeFormat()): string {
  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(d.getTime())) return '';
  const reader = zone ? zoneClock(zone) : null;
  if (!reader) return clockFrom(d.getHours(), d.getMinutes(), format);
  let hour = 0;
  let minute = 0;
  for (const p of reader.formatToParts(d)) {
    if (p.type === 'hour') hour = Number(p.value);
    if (p.type === 'minute') minute = Number(p.value);
  }
  return clockFrom(hour, minute, format);
}

/** The date-fns pattern for the chosen clock: 'HH:mm' or 'h:mm a'. */
export function clockPattern(format: TimeFormat = timeFormat()): string {
  return format === '24h' ? 'HH:mm' : 'h:mm a';
}
